import React from 'react';

/**
 * Design V2 icon set. 16px grid, 1.5px stroke, round joins.
 * Deliberately small: icons are used for actions and channel identity only,
 * never as decoration next to every label.
 */
export type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

const base = ({ size = 16, className, ...rest }: IconProps) => ({
  width: size,
  height: size,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  className,
  ...rest,
});

export const IconSearch = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="7" cy="7" r="4.5" />
    <path d="m13.5 13.5-3.2-3.2" />
  </svg>
);
export const IconPlus = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 3.5v9M3.5 8h9" />
  </svg>
);
export const IconClose = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m4 4 8 8M12 4l-8 8" />
  </svg>
);
export const IconCheck = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m3.5 8.5 3 3 6-7" />
  </svg>
);
export const IconChevronDown = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m4 6 4 4 4-4" />
  </svg>
);
export const IconChevronRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m6 4 4 4-4 4" />
  </svg>
);
export const IconChevronLeft = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m10 4-4 4 4 4" />
  </svg>
);
export const IconArrowUp = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 13V3M4 7l4-4 4 4" />
  </svg>
);
export const IconArrowDown = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 3v10M4 9l4 4 4-4" />
  </svg>
);
export const IconArrowUpDown = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5.5 12.5v-9M3 6l2.5-2.5L8 6M10.5 3.5v9M8 10l2.5 2.5L13 10" />
  </svg>
);
export const IconMore = (p: IconProps) => (
  <svg {...base(p)} strokeWidth={0} fill="currentColor">
    <circle cx="3.5" cy="8" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="12.5" cy="8" r="1.25" />
  </svg>
);
export const IconPhone = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.2 2.5h2.3l1.2 3-1.5 1a7.5 7.5 0 0 0 4.3 4.3l1-1.5 3 1.2v2.3a1 1 0 0 1-1.1 1A11.5 11.5 0 0 1 2.2 3.6a1 1 0 0 1 1-1.1Z" />
  </svg>
);
export const IconMessage = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 3.5h11v7h-6L4.5 13v-2.5h-2v-7Z" />
  </svg>
);
export const IconMail = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
    <path d="m2.5 4.5 5.5 4 5.5-4" />
  </svg>
);
export const IconCalendar = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2" y="3" width="12" height="11" rx="1.5" />
    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" />
  </svg>
);
export const IconClock = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="8" r="5.75" />
    <path d="M8 4.75V8l2.25 1.5" />
  </svg>
);
export const IconUsers = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="6" cy="5.5" r="2.25" />
    <path d="M1.75 13a4.25 4.25 0 0 1 8.5 0M10.5 3.5a2.25 2.25 0 0 1 0 4.4M12.5 9.4a4.2 4.2 0 0 1 1.75 3.6" />
  </svg>
);
export const IconNote = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 2.5h7l3 3v8H3v-11Z" />
    <path d="M10 2.5v3h3M5.5 8h5M5.5 10.5h3" />
  </svg>
);
export const IconSparkle = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 2.5c.4 2.8 1.7 4.1 4.5 4.5-2.8.4-4.1 1.7-4.5 4.5-.4-2.8-1.7-4.1-4.5-4.5 2.8-.4 4.1-1.7 4.5-4.5ZM12.5 10.5c.2 1.1.7 1.6 1.8 1.8-1.1.2-1.6.7-1.8 1.8-.2-1.1-.7-1.6-1.8-1.8 1.1-.2 1.6-.7 1.8-1.8Z" />
  </svg>
);
export const IconWarning = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 2.5 14 13H2L8 2.5Z" />
    <path d="M8 6.5v3M8 11.25v.01" />
  </svg>
);
export const IconInfo = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="8" r="5.75" />
    <path d="M8 7.25V11M8 5v.01" />
  </svg>
);
export const IconFilter = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 3.5h11L9.5 8.5v4l-3 1v-5L2.5 3.5Z" />
  </svg>
);
export const IconEdit = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m9.5 3.5 3 3-7 7H2.5v-3l7-7ZM8.5 4.5l3 3" />
  </svg>
);
export const IconExternal = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6.5 3.5H3.5v9h9v-3M9 2.5h4.5V7M13.5 2.5 8 8" />
  </svg>
);
export const IconUser = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="5.5" r="2.75" />
    <path d="M2.75 14a5.25 5.25 0 0 1 10.5 0" />
  </svg>
);
export const IconFlag = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.5 14V2.5h8l-1.5 3 1.5 3h-8" />
  </svg>
);
export const IconDollar = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 1.75v12.5M11 5A3 3 0 0 0 8 3.5H7.25a2.25 2.25 0 0 0 0 4.5h1.5a2.25 2.25 0 0 1 0 4.5H8A3 3 0 0 1 5 11" />
  </svg>
);
export const IconVoicemail = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="4.25" cy="9" r="2.5" />
    <circle cx="11.75" cy="9" r="2.5" />
    <path d="M4.25 11.5h7.5" />
  </svg>
);
export const IconGrid = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" />
    <rect x="9" y="2.5" width="4.5" height="4.5" rx="1" />
    <rect x="2.5" y="9" width="4.5" height="4.5" rx="1" />
    <rect x="9" y="9" width="4.5" height="4.5" rx="1" />
  </svg>
);
export const IconStore = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 6.5 3.5 3h9l1 3.5M2.5 6.5h11v7h-11v-7ZM6.5 13.5v-4h3v4" />
  </svg>
);
export const IconReceipt = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.5 2.5h9v11l-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1-1.5 1v-11ZM6 6h4M6 8.5h4" />
  </svg>
);
export const IconBox = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m8 2 5.5 3v6L8 14l-5.5-3V5L8 2ZM2.5 5 8 8l5.5-3M8 8v6" />
  </svg>
);
export const IconPlug = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5.5 2v3M10.5 2v3M3.5 5h9v2.5a4.5 4.5 0 0 1-9 0V5ZM8 12v2" />
  </svg>
);
export const IconMenu = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
  </svg>
);
export const IconSidebar = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M6 3v10" />
  </svg>
);
export const IconSun = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1 1M11.6 11.6l1 1M3.4 12.6l1-1M11.6 4.4l1-1" />
  </svg>
);
export const IconMoon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5Z" />
  </svg>
);
export const IconCopy = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
    <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
  </svg>
);
export const IconArchive = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 3.5h11v3h-11zM3.5 6.5v6h9v-6M6.5 9.5h3" />
  </svg>
);
export const IconInbox = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 9.5h3l1 2h3l1-2h3M2.5 9.5 4 3.5h8l1.5 6v4h-11v-4Z" />
  </svg>
);
export const IconPlay = (p: IconProps) => (
  <svg {...base(p)} fill="currentColor" strokeWidth={0}>
    <path d="M5 3.5v9l7-4.5-7-4.5Z" />
  </svg>
);
export const IconArrowRight = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);
export const IconBuilding = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 13.5V3.5a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v10M10 6.5h2a1 1 0 0 1 1 1v6M2 13.5h12M5.5 5h2M5.5 7.5h2M5.5 10h2" />
  </svg>
);
export const IconLayers = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m8 2.5 5.5 3L8 8.5l-5.5-3 5.5-3ZM2.5 8.5 8 11.5l5.5-3M2.5 11 8 14l5.5-3" />
  </svg>
);
export const IconDocument = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.5 2h6l3 3v9h-9V2Z" />
    <path d="M9.5 2v3h3M5.5 8h5M5.5 10.5h5" />
  </svg>
);
export const IconCard = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
    <path d="M2 6.5h12M4.5 10h3" />
  </svg>
);
export const IconRefresh = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M13 8a5 5 0 0 1-8.7 3.4M3 8a5 5 0 0 1 8.7-3.4" />
    <path d="M11.5 2v2.75H8.75M4.5 14v-2.75h2.75" />
  </svg>
);
export const IconLock = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3.5" y="7" width="9" height="7" rx="1.5" />
    <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
  </svg>
);
export const IconSettings = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="8" r="2" />
    <path d="M8 1.75v1.5M8 12.75v1.5M1.75 8h1.5M12.75 8h1.5M3.6 3.6l1.05 1.05M11.35 11.35l1.05 1.05M3.6 12.4l1.05-1.05M11.35 4.65 12.4 3.6" />
  </svg>
);
export const IconColumns = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M6 3v10M10 3v10" />
  </svg>
);
export const IconCheckCircle = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8" cy="8" r="5.75" />
    <path d="m5.5 8.25 1.75 1.75 3.25-3.75" />
  </svg>
);
export const IconTask = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
    <path d="m5.25 8.25 1.75 1.75 3.75-4" />
  </svg>
);
export const IconEye = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M1.75 8s2.25-4.25 6.25-4.25S14.25 8 14.25 8 12 12.25 8 12.25 1.75 8 1.75 8Z" />
    <circle cx="8" cy="8" r="1.75" />
  </svg>
);
export const IconEyeOff = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 2.5l11 11M6.6 6.65a1.9 1.9 0 0 0 2.7 2.7M4.35 4.5C2.7 5.6 1.75 8 1.75 8s2.25 4.25 6.25 4.25c1.15 0 2.15-.35 3-.85M7 3.85c.35-.05.65-.1 1-.1 4 0 6.25 4.25 6.25 4.25s-.55 1.05-1.55 2.1" />
  </svg>
);
export const IconUpload = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M8 10.5V3.5M5 6.5 8 3.5l3 3M3 11v1.25a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V11" />
  </svg>
);
export const IconFile = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 2.75h5l3 3v7.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1ZM9 2.75v3h3" />
  </svg>
);
export const IconSignature = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M2.5 12.5c2-.5 3-2 3.5-4s.5-5-1-5-1.5 3.5-.5 6.5 3 3 4.5.5c.5 1.5 1.5 1.5 2.5 0M11 13.5h2.5" />
  </svg>
);
export const IconGrip = (p: IconProps) => (
  <svg {...base(p)} strokeWidth={0} fill="currentColor">
    <circle cx="6" cy="4" r="1.1" />
    <circle cx="10" cy="4" r="1.1" />
    <circle cx="6" cy="8" r="1.1" />
    <circle cx="10" cy="8" r="1.1" />
    <circle cx="6" cy="12" r="1.1" />
    <circle cx="10" cy="12" r="1.1" />
  </svg>
);
export const IconTrash = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 4.5h10M6.5 4.5v-1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M4.5 4.5l.6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8M6.75 7.5v3.5M9.25 7.5v3.5" />
  </svg>
);
export const IconPause = (p: IconProps) => (
  <svg {...base(p)} fill="currentColor" strokeWidth={0}>
    <rect x="4" y="3.5" width="2.75" height="9" rx="0.75" />
    <rect x="9.25" y="3.5" width="2.75" height="9" rx="0.75" />
  </svg>
);
export const IconLink = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6.5 9.5 9.5 6.5M7 4.5l1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1M9 11.5l-1 1A2.5 2.5 0 0 1 4.5 9l1-1" />
  </svg>
);
